import { Moment } from 'moment';
import { InvalidUrlError } from '../../../common/errors/errors';
import { DEFAULT_LOCATIONS_PAGE_SIZE } from '../../../common/locations/components/LocationsTable';
import { LocationMapper } from '../../../common/locations/mappers/LocationMapper';
import { Location } from '../../../common/locations/models/Location';
import { LOCATIONS_FRAGMENT } from '../../../common/locations/services/LocationsService';
import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { FilterOperator, SorterSet } from '../../../common/types/gql-types';
import { CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { DateUtils } from '../../../common/utils/DateUtils';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { ReportCertificateDetailMapper } from '../mappers/ReportCertificateDetailMapper';
import { ReportCertificateMapper } from '../mappers/ReportCertificateMapper';
import { ReportCertificate } from '../models/ReportCertificate';
import { ReportCertificateDetail } from '../models/ReportCertificateDetail';

export const GET_REPORT_CERTS_QUERY = `
query (
  $reportId: ID!,
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
  $filters: CertificatesFilter,
  $sorters: [CertificatesSorter]
) {
  node(id: $reportId) {
    ... on ReportDone {
      venafiGuidsUpdatedAt
      certificates(
        first: $first,
        last: $last,
        after: $after,
        before: $before,
        filters: $filters,
        sorters: $sorters
      ) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            id
            subject
            issuer
            notAfter
            fingerprint
            venafiGuid
            signatureAlgorithm {
              digest
            }
            publicKey {
              id
              shortDescription
              hasPrivateKey
            }
          }
        }
        totalCount
      }
    }
  }
}
`;

export const GET_REPORT_CERT_QUERY = `
query ($reportId: ID!, $certificateId: ID!, $firstLocations: Int) {
  report: node(id: $reportId) {
    ... on ReportDone {
      venafiGuidsUpdatedAt
    }
  }
  certificate: node(id: $certificateId) {
    ... on Certificate {
      id
      subject
      issuer
      notBefore
      notAfter
      signatureAlgorithm {
        algorithm
        digest
        rsaPadding
      }
      publicKey {
        id
        shortDescription
        hasPrivateKey
      }
      isCa
      isSelfSigned
      encoded
      version
      serialNumber
      signature
      extensions {
        critical
        name
        value
      }
      organizationCertificate {
        id
      }
      locations(first: $firstLocations) {
        ${LOCATIONS_FRAGMENT}
      }
      fingerprint
      venafiGuid
      report {
        id
      }
    }
  }
}
`;

// Provide the last updated timestamp of the report in the certificates
// list response in order to correctly update the UI after a Venafi update
// is requested, without reloading the whole report
type ReportCertificatesResponse = {
  certificates: PaginationResult<ReportCertificate>;
  venafiLastUpdatedAt: Moment | null;
};

export class ReportCertificatesRepository extends BaseGQLService {
  private readonly certificateMapper = new ReportCertificateMapper();
  private readonly certificateDetailMapper = new ReportCertificateDetailMapper();
  private readonly locationMapper = new LocationMapper();

  async getList(reportId: number, cursor: CursorInfo, filters: FilterOperator | null, sorters: SorterSet[]): Promise<ReportCertificatesResponse> {
    return this.query(
      GET_REPORT_CERTS_QUERY,
      {
        reportId: this.gqlIdEncoder.encode(ModelType.Report, reportId),
        ...this.mapCursorInfo(cursor),
        filters,
        sorters,
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not find Report ${reportId}`);
        }
        const { certificates, venafiGuidsUpdatedAt } = data.node;
        const { edges, pageInfo, totalCount } = certificates;
        return {
          certificates: {
            data: edges.map(({ node }: any) => this.certificateMapper.mapGQLModel(node, reportId, venafiGuidsUpdatedAt)),
            pageInfo,
            totalCount,
          },
          venafiLastUpdatedAt: venafiGuidsUpdatedAt ? DateUtils.toMoment(venafiGuidsUpdatedAt) : null,
        };
      }
    );
  }

  async getCertificate(
    reportId: number,
    certificateId: number
  ): Promise<{ certificate: ReportCertificateDetail; initialLocations: PaginationResult<Location> }> {
    const rId = this.gqlIdEncoder.encode(ModelType.Report, reportId);
    const cId = this.gqlIdEncoder.encode(ModelType.Certificate, certificateId);
    return this.query(
      GET_REPORT_CERT_QUERY,
      {
        reportId: rId,
        certificateId: cId,
        firstLocations: DEFAULT_LOCATIONS_PAGE_SIZE,
      },
      (data) => {
        if (!data.certificate) {
          throw new InvalidUrlError(`Invalid request: could not find Certificate ${certificateId}`);
        }
        if (!data.report) {
          throw new InvalidUrlError(`Invalid request: could not find Report ${reportId}`);
        }
        const { locations, ...certificate } = data.certificate;
        if (certificate.report.id !== rId) {
          // We are retrieving the certificate by ID, but we need to ensure that the URL is correct.
          // If we navigate to a URL such as `/report/<id_1>/certificates/<cert_id_of_report_2>`,
          // the request would still resolve correctly even if the path access is wrong.
          throw new InvalidUrlError(`Invalid request: Certificate ${certificateId} does not belong to Report ${reportId}`);
        }
        return {
          certificate: this.certificateDetailMapper.mapGQLModel(certificate, data.report.venafiGuidsUpdatedAt),
          initialLocations: {
            pageInfo: locations.pageInfo,
            totalCount: locations.totalCount,
            data: locations.edges.map((e: any) => this.locationMapper.mapGQLModel(e.node)),
          },
        };
      }
    );
  }
}
